import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/lib/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { 
  Users,
  UserPlus,
  Shield,
  ShieldCheck,
  UserX,
  UserCheck,
  Key,
  Trash2,
  ArrowLeft,
  Loader2,
  LogOut,
  Clock
} from "lucide-react";
import { cn } from "@/lib/utils";

export default function UserManagementPage() {
  const navigate = useNavigate();
  const { admin, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Form states
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("staff");
  const [resetPassword, setResetPassword] = useState("");

  useEffect(() => {
    // Check if user is OWNER (accept both "owner" and "admin" for backward compatibility)
    if (admin && admin.role !== "owner" && admin.role !== "admin") {
      toast.error("Acces interzis");
      navigate("/admin");
      return;
    }
    fetchUsers();
  }, [admin]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get("/admin/users");
      setUsers(response.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Nu ai permisiunea de a accesa această pagină");
        navigate("/admin");
      } else {
        toast.error("Eroare la încărcarea utilizatorilor");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUsername || !newPassword) {
      toast.error("Completează toate câmpurile");
      return;
    }
    
    try {
      setActionLoading(true);
      await api.post("/admin/users", {
        username: newUsername,
        password: newPassword,
        role: newRole
      });
      toast.success(`Utilizator ${newUsername} creat cu succes`);
      setShowAddModal(false);
      setNewUsername("");
      setNewPassword("");
      setNewRole("staff");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la crearea utilizatorului");
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.status === "active" ? "disabled" : "active";
    try {
      await api.put(`/admin/users/${user.id}`, { status: newStatus });
      toast.success(`Utilizator ${user.username} ${newStatus === "active" ? "activat" : "dezactivat"}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la actualizarea statusului");
    }
  };

  const handleChangeRole = async (user, newRole) => {
    try {
      await api.put(`/admin/users/${user.id}`, { role: newRole });
      toast.success(`Rolul utilizatorului ${user.username} schimbat în ${newRole.toUpperCase()}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la schimbarea rolului");
    }
  };

  const handleResetPassword = async () => {
    if (!resetPassword || resetPassword.length < 8) {
      toast.error("Parola trebuie să aibă minim 8 caractere");
      return;
    }
    
    try {
      setActionLoading(true);
      await api.post(`/admin/users/${selectedUser.id}/reset-password`, {
        new_password: resetPassword
      });
      toast.success(`Parola pentru ${selectedUser.username} a fost resetată`);
      setShowResetModal(false);
      setSelectedUser(null);
      setResetPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la resetarea parolei");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!confirm(`Ești sigur că vrei să ștergi permanent utilizatorul ${user.username}? Această acțiune nu poate fi anulată.`)) {
      return;
    }
    
    try {
      await api.delete(`/admin/users/${user.id}`);
      toast.success(`Utilizator ${user.username} șters definitiv`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la ștergerea utilizatorului");
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success("Deconectat cu succes");
    window.location.href = "/admin/login";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Niciodată";
    return new Date(dateStr).toLocaleString("ro-RO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#22c55e] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="bg-[#0f172a] border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" className="text-slate-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#22c55e] rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-[#020617]" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">User Management</h1>
                <p className="text-sm text-slate-400">Gestionează utilizatorii Admin</p>
              </div>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="text-slate-400 hover:text-red-400"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-[#0f172a] border border-slate-800 rounded-lg p-4">
            <p className="text-slate-400 text-sm">Total Utilizatori</p>
            <p className="text-2xl font-bold text-white">{users.length}</p>
          </div>
          <div className="bg-[#0f172a] border border-slate-800 rounded-lg p-4">
            <p className="text-slate-400 text-sm">Activi</p>
            <p className="text-2xl font-bold text-green-500">
              {users.filter(u => u.status === "active").length}
            </p>
          </div>
          <div className="bg-[#0f172a] border border-slate-800 rounded-lg p-4">
            <p className="text-slate-400 text-sm">Dezactivați</p>
            <p className="text-2xl font-bold text-red-500">
              {users.filter(u => u.status === "disabled").length}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-white">Utilizatori</h2>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-[#22c55e] hover:bg-[#16a34a] text-black"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Adaugă Utilizator
          </Button>
        </div>

        {/* Users Table */}
        <div className="bg-[#0f172a] border border-slate-800 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700 hover:bg-transparent">
                <TableHead className="text-slate-400">Username</TableHead>
                <TableHead className="text-slate-400">Rol</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400">Ultima Conectare</TableHead>
                <TableHead className="text-slate-400">Creat</TableHead>
                <TableHead className="text-slate-400 text-right">Acțiuni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} className="border-slate-700 hover:bg-slate-800/50">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {user.role === "owner" ? (
                        <ShieldCheck className="w-4 h-4 text-amber-500" />
                      ) : (
                        <Shield className="w-4 h-4 text-slate-500" />
                      )}
                      <span className="text-white font-medium">{user.username}</span>
                      {user.id === admin?.id && (
                        <span className="text-xs bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">Tu</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {user.id === admin?.id ? (
                      <span className="text-amber-500 font-medium uppercase">{user.role}</span>
                    ) : (
                      <Select
                        value={user.role}
                        onValueChange={(value) => handleChangeRole(user, value)}
                      >
                        <SelectTrigger className="w-24 h-8 bg-slate-800 border-slate-700 text-white text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="owner" className="text-amber-500">OWNER</SelectItem>
                          <SelectItem value="staff" className="text-slate-300">STAFF</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={cn(
                      "px-2 py-1 rounded text-xs font-medium",
                      user.status === "active" 
                        ? "bg-green-500/20 text-green-400" 
                        : "bg-red-500/20 text-red-400"
                    )}>
                      {user.status === "active" ? "Activ" : "Dezactivat"}
                    </span>
                  </TableCell>
                  <TableCell className="text-slate-400 text-sm">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(user.last_login)}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-500 text-sm">
                    {formatDate(user.created_at)}
                    {user.created_by && (
                      <span className="block text-xs text-slate-600">de {user.created_by}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {user.id !== admin?.id && (
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleToggleStatus(user)}
                          className={user.status === "active" 
                            ? "text-red-400 hover:text-red-300 hover:bg-red-500/10" 
                            : "text-green-400 hover:text-green-300 hover:bg-green-500/10"
                          }
                          title={user.status === "active" ? "Dezactivează" : "Activează"}
                        >
                          {user.status === "active" ? (
                            <UserX className="w-4 h-4" />
                          ) : (
                            <UserCheck className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowResetModal(true);
                          }}
                          className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                          title="Resetează parola"
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteUser(user)}
                          className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                          title="Șterge"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>

      {/* Add User Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-[#0f172a] border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#22c55e]" />
              Adaugă Utilizator Nou
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Creează un cont nou pentru personalul de birou
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Username</Label>
              <Input
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                placeholder="ex: maria.office"
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Parolă</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minim 8 caractere"
                className="bg-slate-800 border-slate-700 text-white"
              />
              <p className="text-xs text-slate-500">Utilizatorul va fi obligat să schimbe parola la prima conectare</p>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Rol</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="staff">STAFF - Acces standard</SelectItem>
                  <SelectItem value="owner">OWNER - Acces complet</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)} className="border-slate-700 text-slate-300">
              Anulează
            </Button>
            <Button 
              onClick={handleAddUser} 
              disabled={actionLoading}
              className="bg-[#22c55e] hover:bg-[#16a34a] text-black"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Creează Utilizator"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={showResetModal} onOpenChange={setShowResetModal}>
        <DialogContent className="bg-[#0f172a] border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" />
              Resetează Parola
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Setează o parolă nouă pentru <span className="text-white font-medium">{selectedUser?.username}</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Parolă Nouă</Label>
              <Input
                type="password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                placeholder="Minim 8 caractere"
                className="bg-slate-800 border-slate-700 text-white"
              />
              <p className="text-xs text-slate-500">Utilizatorul va fi obligat să schimbe parola la următoarea conectare</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetModal(false)} className="border-slate-700 text-slate-300">
              Anulează
            </Button>
            <Button 
              onClick={handleResetPassword} 
              disabled={actionLoading}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Resetează Parola"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
